/**
 * Invoices — functional operator list + compile entry (A4 Step 4).
 *
 * Route: /t/:tenantSlug/invoices
 *
 * Lists the tenant's invoices (click through to the preview) and, for owners,
 * a minimal compile form (client picker + period) that creates a draft and
 * opens its preview. De-tech pass (founder soak 2026-07-06): the raw
 * client-uuid text input became a proper client <select> fed by
 * useClientsList (the TS-1 roster read; native-select house pattern per
 * AddClientDialog), and the copy dropped the engine words. Gate/behavior
 * unchanged: compile stays owner-only and still posts client_id.
 *
 * UX-8 proof migration: composed over PageShell (the locked gate-as-a-prop
 * contract — hooks above, gate consumed inside the shell) + AsyncBoundary
 * (list loading/error/empty) + Button + Field, re-skinned from the raw
 * stone-N / red-N scaffolding onto tokens (this page was explicitly
 * unbranded pending its branding slice; both grandfather-allowlist entries
 * removed with this change).
 *
 * PRECONDITION (operator-discipline, design plan D1): run the A3 reconcile and
 * confirm CLEAN for the period before compiling.
 *
 * Invoice workflow pass (founder feedback day-1 live, 2026-07-10): rows
 * lead with the client's name (doc # secondary), statuses render Title
 * Case via the shared invoiceStatus helper, and a quiet expandable
 * legend explains what each status means.
 *
 * Per-row actions (invoice void affordance): draft -> Discard (confirm,
 * shares discardDraftInvoice with InvoicePreview's Discard); approved ->
 * Reopen (non-destructive, no confirm — matches Preview); issued -> Void
 * (the VoidInvoiceDialog also used on Preview — reason required, frees the
 * events back to the client's Pre-billing tab); void -> no action. Preview
 * stays the row's primary link (unchanged).
 *
 * THE BILLING COCKPIT (founder 2026-08-31) gave the page the app's WIDE
 * width; its band of six panels above the table is gone since LP-34
 * (Amanda, 2026-10-26: "the invoicing page needs a full UX workup, it's
 * a mess"). Display and layout only: every act, gate and read is as it
 * was.
 *
 *   BAND  the month picker; Pre-billing for every client and Payroll
 *         hours, the filled buttons (LP-39: used more than the billing
 *         file); then the month's act in outline, Compile and the billing
 *         file; and the QuickBooks connection as one quiet line.
 *   BODY  one column: the outcome banners; what Compile will do and did
 *         (one summary line, the names behind "See details"); the month
 *         line of counts; the filters on one row (Showing, Only this
 *         client, the status meanings); then the table, which is the
 *         page, inside the house overflow-x-auto valve, each row with
 *         one primary (Preview) and a menu for the rest; then the
 *         unbilled-work section and the one-client form, both folded.
 *
 * The body carries min-w-0 so the money table can never push the page
 * wider than its frame.
 */

import { type ReactNode, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import {
  canViewInvoice,
  canViewMasterPrebilling,
  canCompileInvoice,
  canApproveInvoice,
  canIssueInvoice,
  canVoidInvoice,
  canOpenEveryClientRecord,
  canViewAllClinical,
  canPushInvoice,
  canExportPayrollHours,
} from '@/lib/capabilities';
import {
  invoicesApi,
  unbilledApi,
  ApiError,
  type InvoiceListWire,
  type InvoiceListRowWire,
  type UnbilledReason,
  type UnbilledWorkRowWire,
  type UnbilledWorkWire,
} from '@/lib/api';
import { invalidateInvoiceReads } from '@/lib/invoiceInvalidation';
import { proseDate, rowDate } from '@/lib/proseDate';
import { useClientsList } from '@/lib/clientsApi';
import { PAGE_GUTTER, PAGE_WIDTH_CLASSES, PageShell } from '@/components/primitives/PageShell';
import { PaginationFooter } from '@/components/primitives/PaginationFooter';
import { SectionBand } from '@/components/primitives/SectionBand';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Field } from '@/components/primitives/Field';
import { TextInput } from '@/components/primitives/TextInput';
import { Button, buttonClassName } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { DestructiveTextAction, TextAction, textActionClass } from '@/components/workspace/TextAction';
import { ConfirmDialog } from '@/components/workspace/ConfirmDialog';
import { VoidInvoiceDialog } from '@/components/VoidInvoiceDialog';
import { QboConnectionSummary } from '@/components/billing/QboConnectionSummary';
import { QboConnectResultBanner } from '@/components/billing/QboConnectResultBanner';
import { BatchPushBar } from '@/components/billing/BatchPushBar';
import {
  CompileMonthNotes,
  CompileMonthReportView,
  CompileMonthRunButton,
  useCompileMonth,
} from '@/components/billing/CompileMonthAction';
import {
  ExportMonthProgress,
  ExportMonthReportView,
  ExportMonthRunButton,
  useExportMonth,
} from '@/components/billing/ExportMonthAction';
import { AnchoredLayer } from '@/components/billing/AnchoredLayer';
import { RowMenu } from '@/components/billing/RowMenu';
import {
  INVOICE_STATUS_LEGEND,
  QBO_COLUMN_LEGEND,
  invoiceStatusLabel,
  invoiceStatusVariant,
} from '@/lib/invoiceStatus';
import { noBillableEventsExplanation, readNativeBillingFrom } from '@/lib/prebilling';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useSupportContact } from '@/hooks/useSupportContact';
import { FALLBACK_SUPPORT_CONTACT, billingHouseholdText, contactSentence, pageWriteText, type SupportContact } from '@/lib/errorCopy';
import { NativeBillingOffBanner } from '@/components/billing/NativeBillingOffBanner';
import { VoidCopiesOpenBanner } from '@/components/billing/VoidCopiesOpenBanner';
import { PushFailedAlert } from '@/components/billing/PushFailedAlert';
import { useQboPushState } from '@/components/billing/QboPushPanel';
import { pushStatusCopy } from '@/lib/qboPushStatus';
import {
  voidCopyStatusCopy,
  voidCopyPaymentApplied,
  rowNeedsSending,
  rowNeedsQboFix,
  rowNeedsUnconfirmed,
} from '@/lib/qboCopyStatus';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

// max-w-full: a control keeps its own content width, so its text is never
// clipped, and is capped at its field when the line is narrower (the two
// control rows give their fields min-w-0), so nothing pushes the page
// sideways.
const INPUT = 'max-w-full border border-sage-200 rounded-tight px-2 py-1 text-sm bg-paper text-ink';

/** Same page size as the ruled pattern the footer is extracted from
 *  (CompletedView): if we do it for one list, we do it for all. */
const PAGE_SIZE = 25;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/** 'YYYY-MM' for a local calendar instant. */
function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 'YYYY-MM' shifted by whole months (delta may be negative). */
function shiftMonthKey(key: string, delta: number): string {
  const [y, m] = key.split('-').map((part) => Number.parseInt(part, 10));
  const index = (y ?? 0) * 12 + ((m ?? 1) - 1) + delta;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/** 'January 2026' for the heading that names a non-current month. */
function monthHeadingLabel(key: string): string {
  const [y, m] = key.split('-').map((part) => Number.parseInt(part, 10));
  return `${MONTH_NAMES[(m ?? 1) - 1]} ${y}`;
}

/** An invoice's period as it reads in the table: "Jun 1 to Jun 30",
 *  with the years only when the period leaves the picked month's year. */
function periodLabel(start: string, end: string, monthKey: string): string {
  const year = monthKey.slice(0, 4);
  const withYear = start.slice(0, 4) !== year || end.slice(0, 4) !== year;
  return `${rowDate(start, withYear)} to ${rowDate(end, withYear)}`;
}

// ---------------------------------------------------------------------
// THE UNBILLED LINE (contract item 3). The list below is FROM invoices,
// so work that never reached an invoice has no row and raises no count.
// This line is the read that looks for the money that is NOT there.
// ---------------------------------------------------------------------

/** Each reason names the ACTION that clears it, never the finding and
 *  never the wire token. Each line names the state and the next action,
 *  in the operator's words: no internal vocabulary, and no source-system
 *  name, which is tenant-specific and would not survive a second tenant.
 *
 *  reconcile_first can only reach a month BEFORE billing.native_from.
 *  The importer refuses any period reaching that date and a note before
 *  it creates no event, so a month is one era or the other and never
 *  both. Founder ruling 2026-08-30: Olive REPLACES the imported source,
 *  there is no overlap. */
function unbilledReasonCopy(
  reason: Exclude<UnbilledReason, 'no_ready_notes'>,
  row: UnbilledWorkRowWire,
  monthLabel: string,
  contact: SupportContact,
): string {
  switch (reason) {
    case 'reconcile_first':
      return contactSentence(
        "This charge's hours came from the old system and were never signed off, so it can't go on an invoice here. Ask {contact_billing} to bill it by hand.",
        contact,
      );
    case 'invoice_not_sent':
      // Add unbilled charges reaches only the charges inside the unsent
      // invoice's own period, so a straddler says which charges it can
      // take and what happens to the rest.
      return row.unsent_covers_month !== false
        ? `This work isn't on the ${monthLabel} invoice, which isn't sent yet. Open it and choose Add unbilled charges, pressing Reopen first if it's approved.`
        : `This work isn't billed yet, because the unsent invoice covers only part of ${monthLabel}. Use Add unbilled charges on it for work in its dates, and compile the rest after it's sent.`;
    case 'invoice_already_issued':
      return `This client's ${monthLabel} invoice has already been sent. Compile makes a second ${monthLabel} invoice for this work.`;
    case 'not_invoiced':
      return "Use Compile above to build this month's invoice.";
    case 'rate_card':
      return "A note this month isn't on the invoice because the client has no rate for that kind of work. Add the rate on their Rates card.";
  }
}

/** One reason's line for a row, in the picked month's words:
 *  no_ready_notes names how many notes wait. */
function unbilledReasonLine(
  reason: UnbilledReason,
  row: UnbilledWorkRowWire,
  monthLabel: string,
  contact: SupportContact,
): string {
  if (reason === 'no_ready_notes') {
    return `No notes ready yet: ${row.not_ready_count} waiting on Pre-billing.`;
  }
  return unbilledReasonCopy(reason, row, monthLabel, contact);
}

/** The tenant-wide period note. Gated on the COUNT, never the boundary:
 *  a native_from that falls mid-month still leaves the Ready notes dated
 *  before it off every invoice. Deliberately tenant-wide wording that
 *  names nobody; the per-client export-only sentences live on the
 *  Pre-billing tab. Wording: Amanda, LP-34 round two. */
function preNativeNote(count: number, nativeFrom: string | null): string {
  const notes = count === 1 ? '1 note' : `${count} notes`;
  const go = count === 1 ? 'goes' : 'go';
  return nativeFrom === null
    ? `${notes} ${go} out in the billing CSV, not on an invoice, until invoices in Olive are turned on.`
    : `${notes} from before ${proseDate(nativeFrom)} ${go} out in the billing CSV, not on an invoice.`;
}

/** The month line's title: the house label register. */
const GLANCE_TITLE = 'text-title-sm font-semibold uppercase tracking-label text-sage-800';

// The table's header row, in the house table language (ClientRatesTab,
// RateBandMatrixTab, PreBilling — byte-identical geometry). Those files
// spell the size as an arbitrary 11-pixel step only because they sit on
// tools/type-scale-allowlist.txt; THIS file carries no px-text
// allowance, so the same step comes from the minted rem tokens
// (--text-label / --tracking-label). 200% zoom is a launch requirement
// and px silently ignores the reader's text-size setting.
const TABLE_HEAD_ROW = 'text-left text-label font-semibold uppercase tracking-label text-sage-400';

function rowActionErrorMessage(err: unknown, contact: SupportContact): string {
  // A billing household refusal (0215) carries its own sentence.
  const household = billingHouseholdText(err, FALLBACK_SUPPORT_CONTACT);
  if (household !== null) return household;
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const code = (err.body as { error?: string }).error;
    if (code === 'invoice_not_draft' || code === 'illegal_transition') {
      return "Someone changed that invoice while you had it open, so yours didn't go through. Reload the page to see where it stands.";
    }
    if (code === 'invoice_not_found') {
      return 'That invoice is gone, so nothing changed. Reload the page to see the current list.';
    }
  }
  return contactSentence(
    "That didn't go through, so the invoice is unchanged. Try again, and if it happens again, tell {contact_system}.",
    contact,
  );
}

export function compileError(err: unknown, contact: SupportContact, payerName: string | null = null): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const code = (err.body as { error?: string }).error;
    // A partner's compile (0215, R8): name the payer when the page already
    // holds this client's unbilled row with its bills_with; otherwise the
    // surface's own sentence. Never a name from the response.
    if (code === 'client_bills_with' && payerName !== null && payerName !== '') {
      return `This client shares ${payerName}'s invoice, so no invoice of its own was made. Compile ${payerName}'s invoice, and this client's work goes on it.`;
    }
    const household = billingHouseholdText(err, contact);
    if (household !== null) return household;
    switch (code) {
      case 'no_billable_events':
        // Explained when pre-window Ready notes caused the zero (founder
        // feedback 2026-07-13); a genuinely empty period keeps the plain copy.
        return (
          noBillableEventsExplanation(err.body) ??
          'Nothing is ready to bill for that client and those dates, so no invoice was made. Check that their notes are marked Ready, then compile again.'
        );
      case 'invoice_exists_for_period':
        // The guard refuses a draft or approved invoice that OVERLAPS the
        // period (a sent one never blocks: compile makes a second invoice),
        // so the copy must not claim the periods match when the operator
        // typed a straddling range.
        return "This client already has an invoice for some or all of that period that isn't sent yet. Open that invoice and choose Add unbilled charges.";
      case 'client_not_found':
        return "That client can't be found, so no invoice was made. Pick one from the list and compile again.";
      case 'invalid_body':
        return "That client or those dates can't be used, so no invoice was made. Pick a client from the list and choose a start and end date.";
      case 'reconcile_not_cleared':
        return contactSentence(
          'This month has imported hours that were never signed off, so no invoice was made. Ask {contact_billing} to sign them off, then compile again.',
          contact,
        );
      default:
        return pageWriteText(err, contact);
    }
  }
  return pageWriteText(err, contact);
}

/**
 * One invoice's QuickBooks state, with the act that clears it.
 *
 * The label is the chip; the detail is its title, so the operator gets
 * "what do I do" on hover without the table growing a paragraph column.
 * Tone is semantic, not decorative: `unsent` is the quiet majority case
 * at month end and must not scream, while `blocked` is a real problem
 * with a named remedy.
 */
/** One data-tone attribute, one class map.
 *
 *  THE DANGER REGISTER (founder ruling 2026-09-01: "you can add a danger
 *  color"). This map used to render `mismatch`, `urgent` and `blocked`
 *  in one colour, which made
 *
 *    "a rate band is unmapped, nothing was sent, fixable in thirty
 *     seconds"
 *
 *  typographically identical to
 *
 *    "a voided invoice's copy is still open in QuickBooks with a payment
 *     applied, and voiding it there strands a family's money as
 *     unapplied cash".
 *
 *  Two of those are the same colour of problem and one is not.
 *
 *  A COLOUR MAPPING only. No state changed which tone key it emits and
 *  neither registry (qboPushStatus.ts, qboCopyStatus.ts) was touched:
 *  the split is between keys that already existed and already meant
 *  different things.
 *
 *    danger  MONEY MAY ALREADY BE WRONG. `mismatch` is at Intuit with a
 *            total that disagrees with the frozen snapshot (and its
 *            sibling total_unverifiable, which nothing ever compared);
 *            `urgent` is the open copy with a payment applied. Both are
 *            rows where the harm may already have landed and the obvious
 *            next click makes it worse.
 *    pepper- NOTHING WAS SENT, AND A PERSON CAN FIX IT: band unmapped,
 *    deep    config incomplete, client not linked, the token states.
 *
 *  See src/components/primitives/Chip.tsx for the four-register law and
 *  src/index.css for the token's measured contrast ratios. */
const TONE_CLASS: Record<string, string> = {
  mismatch: 'text-danger font-semibold',
  urgent: 'text-danger font-semibold',
  sent: 'text-sage-800',
  quiet: 'text-sage-800',
  blocked: 'text-pepper-deep font-medium',
  working: 'text-ink-soft',
  unknown: 'text-ink-soft',
  none: 'text-ink-soft',
  unsent: 'text-ink',
};

function PushStatusCell({
  row,
  now,
  contact,
}: {
  row: InvoiceListRowWire;
  now: Date;
  contact: SupportContact;
}): ReactNode {
  // A draft or approved invoice has nothing to send yet. Rendering
  // "Not sent" against them would turn the column into noise on every
  // row that is working exactly as intended.
  if (row.status === 'draft' || row.status === 'approved') {
    return (
      <span data-testid={`invoice-qbo-status-${row.id}`} data-tone="none" className="text-ink-soft">
        –
      </span>
    );
  }

  // A VOID INVOICE IS NOT NOTHING. This column used to answer the en
  // dash here too, on the reasoning that a voided invoice will never be
  // sent — true, and beside the point. Olive's push is create-only, so
  // voiding here does not close the copy in QuickBooks: that copy stays
  // live, keeps collecting payment, and Olive has just freed this
  // invoice's events to be re-billed onto a new one. The exact row the
  // #90 ruling exists for was the blankest cell on the page.
  if (row.status === 'void') {
    const copy = voidCopyStatusCopy(row, now);
    return (
      <>
        <span
          data-testid={`invoice-qbo-status-${row.id}`}
          data-tone={copy.tone}
          title={copy.detail}
          className={`text-xs ${TONE_CLASS[copy.tone] ?? 'text-ink'}`}
        >
          {copy.label}
        </span>
        {/* THE URGENT STATES SAY IT ON THE ROW. A cancelled document
            still live in QuickBooks, possibly with a payment applied, is
            the one row where the obvious next click strands a family's
            money; its sentence is the guard, and a guard in a tooltip is
            not read. Every other void state keeps the label and the
            hover. */}
        {copy.tone === 'urgent' && (
          <p
            data-testid={`invoice-qbo-detail-${row.id}`}
            className="mt-0.5 max-w-xs text-xs text-danger"
          >
            {copy.detail}
          </p>
        )}
      </>
    );
  }

  const copy = pushStatusCopy(row.qbo_push_status, row.qbo_last_error, row.qbo_invoice_id, contact);
  // 'mismatch' is deliberately the loudest: the invoice IS at Intuit, so
  // it can never read as unsent, but its total disagrees with the frozen
  // snapshot. Money has already moved on a number nobody reconciled,
  // which is worse than an invoice that has not gone out yet.
  const tone = TONE_CLASS[copy.tone] ?? 'text-ink';

  return (
    <span
      data-testid={`invoice-qbo-status-${row.id}`}
      data-tone={copy.tone}
      title={copy.detail}
      className={`text-xs ${tone}`}
    >
      {copy.label}
    </span>
  );
}

export function Invoices(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const contact = useSupportContact();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canView = canViewInvoice(whoami);
  const canSeeMasterPrebilling = canViewMasterPrebilling(whoami);
  const canCompile = canCompileInvoice(whoami);
  // The all-clients clinical width, read ONCE: it enables the roster
  // read below and drives the unbilled line's request-or-refusal.
  const canClinical = canViewAllClinical(whoami);
  // Native billing OFF for the workspace (billing.native_from unset or
  // malformed) fails loud on this page too. Read from the tenant config,
  // the shared cache every page holds, and not from the unbilled wire:
  // that read is gated on view_all_clinical and can be refused, and the
  // banner is a tenant fact, not a month-report fact. An unknown config
  // (still loading, or the read failed) never shouts.
  const { data: tenantConfig } = useTenantConfig(tenantSlug, { enabled: canView });
  const nativeBillingOff =
    tenantConfig !== undefined && readNativeBillingFrom(tenantConfig.config) === null;
  // THE CSV MONTHS (LP-34 round two, Amanda 2026-10-26). A month wholly
  // before billing.native_from bills through the billing CSV, never an
  // invoice: the page leads with that sentence, the CSV is its act (in
  // the band's outline weight, as in every month since LP-39), and
  // Compile, the filters and the one-client form step aside. A
  // month that holds native_from (mid-month) is an invoice month. An
  // unknown config (loading, or the read failed) is never a CSV month.
  const nativeFrom = tenantConfig !== undefined ? readNativeBillingFrom(tenantConfig.config) : null;
  const nativeMonth = nativeFrom?.slice(0, 7) ?? null;

  const [clientId, setClientId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  // The unbilled line's expand state (item 3): collapsed to start, like
  // the legend beside it, because the list's job is still the month.
  const [showUnbilled, setShowUnbilled] = useState(false);
  // THE MONTH-END CHECKLIST (founder ruling 2026-08-27; third pile
  // 2026-08-31). The column tells you a row's state; this turns the page
  // into the list of what is left.
  // Off by default: the list's job is still the month, not the exceptions.
  // ONE filter with four positions, not three independent toggles.
  // Clicking another pile SWITCHES to it: the piles are disjoint by
  // construction, so intersecting them would produce an empty table,
  // which is the first thing an operator does at month end and the first
  // thing that would look broken.
  const [monthEndFilter, setMonthEndFilter] =
    useState<'none' | 'send' | 'qbo' | 'unconfirmed'>('none');
  // THE LIFECYCLE SCOPE (D1(b), founder ruling 2026-08-31). The list
  // defaults to the month's LIVE invoices; "Voided" is a named scope the
  // operator can visit. Rows only, never the counts, and an active pile
  // overrides it server-side (the composition rule lives in the repo).
  // 'all' exists on the wire but not on this switcher: two real places
  // are what the operator asked for, and the console rework is a later
  // train.
  const [scope, setScope] = useState<'live' | 'void'>('live');
  // THE MONTH FILTER (contract item 5). A month picker, deliberately NOT a
  // second "Period start"/"Period end" pair: the compile form already owns
  // that pair and it CREATES an invoice, and two identically labelled date
  // controls on one screen, one of which bills a family, is a money
  // hazard. The default is the CURRENT calendar month and is load-bearing:
  // it is what lets the month-end counts reach zero (F3), and what makes
  // "Before this month closes" a true sentence. The clock is read once at
  // mount; every expectation about "this month" on the page compares to
  // that one read.
  const [currentMonth] = useState(() => monthKeyOf(new Date()));
  const [month, setMonth] = useState(currentMonth);
  // The list filter, labelled "Only this client" so it can never be read
  // as the compile form's "Client", which bills. It narrows the ROWS; the
  // month-end counts deliberately ignore it (a count scoped to a client
  // turns "zero for this client" into a false "the month can close").
  const [clientFilter, setClientFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [rowActionError, setRowActionError] = useState<string | null>(null);
  const [discardTarget, setDiscardTarget] = useState<InvoiceListRowWire | null>(null);
  const [voidTarget, setVoidTarget] = useState<InvoiceListRowWire | null>(null);
  // THE INVOICE JUST VOIDED FROM THIS LIST. The void response carries
  // the invoice and nothing about its QuickBooks copy, and the list
  // refetch cannot carry it either: under the live scope the voided row
  // has left the page. So the page holds the id and reads the same
  // push-state query the preview reads, and names the invoice in the
  // banner once that read says the copy was seen open. Derived from
  // data on every render; nothing is stored anywhere else.
  const [justVoided, setJustVoided] = useState<{ id: string; docNumber: string | null } | null>(
    null,
  );
  // An empty status disables the read; only a void invoice is asked about.
  const justVoidedState = useQboPushState(justVoided?.id ?? '', justVoided === null ? '' : 'void');
  const justVoidedCopy = justVoidedState.data?.void_check?.state;
  const namedVoid =
    justVoided !== null && (justVoidedCopy === 'open' || justVoidedCopy === 'open_with_payment')
      ? justVoided
      : null;
  const canApprove = canApproveInvoice(whoami);
  const canVoid = canVoidInvoice(whoami);
  // The push is a batch over selected issued invoices, and its gate is
  // push_invoice — NOT manage_billing_integration, the owner-only
  // connect/disconnect question. This governs the batch bar AND both
  // selection checkboxes, so asking the connect question here would leave
  // the bookkeeper seat, which holds push_invoice precisely so it can run
  // month end, with no checkboxes and one invoice at a time from the
  // preview page.
  const canPushToQbo = canPushInvoice(whoami);
  // The batch train (founder order 2026-08-31): approve and issue join
  // push as batch acts, so the selection column renders for ANY seat
  // holding at least one of the three act capabilities. Each row's
  // checkbox still rides its own row's act (see actFor below).
  const canIssue = canIssueInvoice(whoami);
  const canBatchSelect = canApprove || canIssue || canPushToQbo;
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());



  // Every filter and the page window travel to the SERVER (item 5, F1):
  // the month, the client, the pile and the offset are all in the query,
  // so a count's click-through reaches every row it counted rather than
  // whatever slice arrived under a default limit. placeholderData keeps
  // the previous page rendered while a filter refetches, so narrowing is
  // instant on the rows already on screen and the server's answer replaces
  // them when it lands.
  const { data, isPending, isError, error, refetch } = useQuery<InvoiceListWire>({
    queryKey: ['invoices', 'list', tenantSlug, month, clientFilter, monthEndFilter, scope, offset],
    queryFn: ({ signal }) =>
      invoicesApi.list(
        tenantSlug,
        {
          month,
          client_id: clientFilter || undefined,
          needs: monthEndFilter === 'none' ? undefined : monthEndFilter,
          // Sent EXPLICITLY, 'live' included, like limit: a view that
          // rides the server's default is a view whose meaning can
          // change under it. The server ignores it while a pile is
          // active (the pile overrides the scope).
          scope,
          limit: PAGE_SIZE,
          offset,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
    enabled: canView,
    // Read afresh on every visit, like the unbilled report below.
    staleTime: 0,
  });

  // THE UNBILLED READ (item 3). Month only: the report follows the month
  // picker and ignores the client filter, the pile filter and the page
  // offset, for the reason already ruled for the two counts beside it.
  // The key is a SIBLING of the list's so the list's per-filter segments
  // never reach it; every list invalidation is widened through
  // invalidateInvoiceReads so this line still refreshes when the list
  // does.
  //
  // A seat without view_all_clinical never fires the request. The server
  // refuses rather than narrows (a 403, never a short client list), and
  // firing a read we know will be refused puts a failed request in the
  // console on every load. The visible refusal below renders off the
  // capability, never off this query's error state.
  // Read afresh on every visit (staleTime 0): a money report must not be
  // served from before a note was marked, by this seat or another.
  const { data: unbilled } = useQuery<UnbilledWorkWire>({
    queryKey: ['invoices', 'unbilled', tenantSlug, month],
    queryFn: ({ signal }) => unbilledApi.list(tenantSlug, month, signal),
    enabled: canView && canClinical,
    staleTime: 0,
  });

  // DOES THE MONTH HOLD ANY INVOICE, live or void (LP-34 round two)? The
  // filters and the status meanings show only when it does: a month with
  // nothing to filter gets the empty state's act instead. The list read
  // above cannot answer it (its scope is the live default, so a month of
  // voided invoices alone reads as empty), so this asks the same route
  // for the month's whole lifecycle, one row, and reads only `total`.
  // Read-only, and keyed under the list's prefix so every list
  // invalidation refreshes it too.
  const { data: monthAny } = useQuery<InvoiceListWire>({
    queryKey: ['invoices', 'list', tenantSlug, 'any', month],
    queryFn: ({ signal }) =>
      invoicesApi.list(tenantSlug, { month, scope: 'all', limit: 1, offset: 0 }, signal),
    enabled: canView,
    staleTime: 0,
  });

  // TWO PILES, because they are two different kinds of work and blending
  // them made the number useless (founder ruling 2026-08-28). What is
  // left to send finishes on this screen in seconds and stays finished.
  // What is wrong in QuickBooks cannot be finished from this screen at
  // all, and sits there until somebody goes over and does it. Added
  // together, the count stayed high for days after everything actionable
  // was done, which is exactly when a person stops reading it.
  //
  // THE NUMBERS ARE THE SERVER'S (item 5, F2/F3): to_send_count and
  // to_fix_count are computed over the WHOLE picked month, never tallied
  // from the page below, and never narrowed by the client or pile filter.
  // The row predicates are still applied to the page's rows, but only to
  // keep the visible table honest while a pile refetch is in flight; they
  // feed no number.
  //
  // The clock is taken ONCE per render and threaded down, so every age on
  // the page is measured from the same instant and two cells can never
  // disagree about what "6d" means.
  const now = new Date();
  const allRows = data?.rows ?? [];
  // The pile predicates re-applied to the page's rows keep the visible
  // table honest while a pile refetch is in flight; they feed no number.
  // The SCOPE deliberately has no belt here: switching scope is a new
  // set like switching month, not a narrowing of rows already on screen,
  // and a client-side lifecycle filter would also have to re-derive the
  // pile-overrides-scope composition the server already owns.
  const visibleRows =
    monthEndFilter === 'send'
      ? allRows.filter(rowNeedsSending)
      : monthEndFilter === 'qbo'
        ? allRows.filter((r) => rowNeedsQboFix(r, now))
        : monthEndFilter === 'unconfirmed'
          ? allRows.filter(rowNeedsUnconfirmed)
          : allRows;
  const toSendCount = data?.to_send_count ?? 0;
  const toFixCount = data?.to_fix_count ?? 0;
  const unconfirmedCount = data?.unconfirmed_count ?? 0;
  // The banner's payment sentence counts the rows ON SCREEN and nothing
  // else: the server sends no such count, and a page with no void rows
  // on it (the live scope) says nothing rather than guessing.
  const paymentAppliedCount = allRows.filter(voidCopyPaymentApplied).length;

  // Every filter change also drops the just-voided name from the banner:
  // the operator has moved on, and the rows they move to say it for
  // themselves.
  const pickMonth = (next: string): void => {
    setMonth(next);
    // A filter change returns to page one: an offset that survives lands
    // the operator on an empty page that reads as lost invoices.
    setOffset(0);
    setJustVoided(null);
  };

  const pickClient = (next: string): void => {
    setClientFilter(next);
    setOffset(0);
    setJustVoided(null);
  };

  const pickPile = (next: 'none' | 'send' | 'qbo' | 'unconfirmed'): void => {
    setMonthEndFilter(next);
    setOffset(0);
    setJustVoided(null);
  };

  const pickScope = (next: 'live' | 'void'): void => {
    setScope(next);
    // A scope click means "show me that scope", so an active pile comes
    // off: the pile overrides the scope server-side, and a switcher that
    // visibly did nothing under a pile would read as broken.
    setMonthEndFilter('none');
    setOffset(0);
    setJustVoided(null);
  };

  // The banner's one act: the Voided scope with the QuickBooks pile on
  // it, so the switcher says where the operator is standing and the
  // table holds exactly the rows the heading counted.
  const showVoidCopies = (): void => {
    pickScope('void');
    pickPile('qbo');
  };

  // Client picker feed — the TS-1 roster read. The endpoint gates on
  // view_all_clinical server-side, so the fetch enables on BOTH the compile
  // gate and that width (owner holds both); a non-compiler fires no request.
  const { clients } = useClientsList(tenantSlug, {
    enabled: canCompile && canClinical,
  });
  const activeClients = clients.filter((c) => c.is_active);

  // The request, and the payer named in a refusal, are taken when Compile
  // is pressed and travel as the mutation's variables: React Query hands a
  // mutation its options in an effect, so a mutationFn reading this
  // render's client or period could compile an earlier render's.
  const compileMut = useMutation({
    mutationFn: (v: { body: Parameters<typeof invoicesApi.compile>[1]; payerName: string | null }) =>
      invoicesApi.compile(tenantSlug, v.body),
    onSuccess: (res) => {
      setFormError(null);
      invalidateInvoiceReads(queryClient, tenantSlug);
      navigate(`/t/${tenantSlug}/invoices/${res.invoice.id}`);
    },
    onError: (e, v) => setFormError(compileError(e, contact, v.payerName)),
  });

  // Discard, reopen and void all change what is unbilled, so the shared
  // helper widens each one to the sibling unbilled key too.
  const invalidateList = (): void => {
    invalidateInvoiceReads(queryClient, tenantSlug);
  };

  const discardMut = useMutation({
    mutationFn: (id: string) => invoicesApi.discard(tenantSlug, id),
    onSuccess: () => {
      setRowActionError(null);
      invalidateList();
    },
    onError: (e) => setRowActionError(rowActionErrorMessage(e, contact)),
  });

  const reopenMut = useMutation({
    mutationFn: (id: string) => invoicesApi.reopen(tenantSlug, id),
    onSuccess: () => {
      setRowActionError(null);
      invalidateList();
    },
    onError: (e) => setRowActionError(rowActionErrorMessage(e, contact)),
  });

  // THE PAYLOAD GATES (contract item 1, F2 - one per act since the
  // batch train). ONE derivation, actFor, drives all of: each row's
  // checkbox, the three subsets the bar receives, the header's
  // all-selected question, and the header's disabled state.
  //
  // Per act, gated on the seat's own capability for that act:
  //   draft                            -> batch approve
  //   approved                         -> batch issue
  //   issued AND not yet in QuickBooks -> batch push
  //
  // The push term keeps its ruled second clause: a pushed invoice is
  // still `issued`, so status alone let a stale selection re-send the
  // invoice Olive just watched land (the refetch after onDone brings it
  // back holding a qbo_invoice_id, and onDone deliberately keeps the
  // selection). Deriving the payloads here makes the stale id drop out
  // on its own - and the same construction protects approve and issue
  // for free, because a row that moved on comes back under a different
  // status and leaves its old act's payload.
  //
  // Over the SAME rows the table renders, not data.rows: with the
  // month-end filter active, select-all must not reach an invoice that
  // is not on the screen.
  const actFor = (r: InvoiceListRowWire): 'approve' | 'issue' | 'push' | null => {
    if (r.status === 'draft') return canApprove ? 'approve' : null;
    if (r.status === 'approved') return canIssue ? 'issue' : null;
    if (r.status === 'issued' && r.qbo_invoice_id == null) return canPushToQbo ? 'push' : null;
    return null;
  };
  const toTarget = (r: InvoiceListRowWire): {
    id: string;
    doc_number: string | null;
    client_display_name: string;
  } => ({
    id: r.id,
    doc_number: r.doc_number,
    client_display_name: r.client_display_name,
  });
  const selectableRows = visibleRows.filter((r) => actFor(r) !== null);
  const selectableIds = new Set(selectableRows.map((r) => r.id));
  const selectedRows = selectableRows.filter((r) => selectedIds.has(r.id));
  const selectedDraftTargets = selectedRows.filter((r) => actFor(r) === 'approve').map(toTarget);
  const selectedApprovedTargets = selectedRows.filter((r) => actFor(r) === 'issue').map(toTarget);
  const selectedTargets = selectedRows.filter((r) => actFor(r) === 'push').map(toTarget);
  const allSelectableSelected =
    selectableRows.length > 0 && selectedRows.length === selectableRows.length;

  const toggleOne = (id: string): void =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllSelectable = (): void =>
    setSelectedIds(allSelectableSelected ? new Set() : new Set(selectableRows.map((r) => r.id)));

  // THE MONTH'S TWO ACTS, as state the page places (LP-34): the buttons
  // sit in the header band, what they will do and what they did sit in
  // the body under it. The runs are the components' own.
  const monthLabel = monthHeadingLabel(month);
  const compileMonth = useCompileMonth({ month, monthLabel, unbilled, onDone: invalidateList });
  const exportMonth = useExportMonth({ month, monthLabel });
  // The not-ready clients are counted in the month line, so the plan
  // notes leave them out here.
  const compileNotes = CompileMonthNotes({ state: compileMonth, showNotReady: false });
  const preNativeMonth = nativeMonth !== null && month < nativeMonth;
  const showCompileActivity = !preNativeMonth;
  const hasActivity =
    (showCompileActivity && (compileNotes.length > 0 || compileMonth.report !== null)) ||
    (exportMonth.visible && (exportMonth.running || exportMonth.report !== null));

  // A narrowed view never loses its controls, whatever the month holds:
  // a filter that hid itself would leave no way back out of it.
  const filtersOn = scope !== 'live' || clientFilter !== '' || monthEndFilter !== 'none';
  const monthHasInvoices = (monthAny?.total ?? 0) > 0 || (data?.total ?? 0) > 0;
  const showFilters = (monthHasInvoices && !preNativeMonth) || filtersOn;
  // An invoice month with no invoice at all: the empty state carries the
  // act (Compile when notes are ready, the way to Pre-billing when not).
  const monthEmpty =
    !preNativeMonth && !filtersOn && !monthHasInvoices && data !== undefined && data.rows.length === 0;
  const compileInEmpty = monthEmpty && compileMonth.visible && compileMonth.candidates.length > 0;
  const compileInBand = !preNativeMonth && !compileInEmpty;

  // The clients whose month still has notes to mark Ready, from the
  // month's unbilled report (the server's, never the page's rows).
  const notReadyClientCount =
    canClinical && unbilled !== undefined
      ? unbilled.rows.filter((r) => r.reasons.includes('no_ready_notes')).length
      : 0;
  const pilesShown =
    canView &&
    data !== undefined &&
    (toSendCount > 0 || toFixCount > 0 || unconfirmedCount > 0 || monthEndFilter !== 'none');
  const notReadyText =
    notReadyClientCount === 1
      ? '1 client has notes to mark Ready'
      : `${notReadyClientCount} clients have notes to mark Ready`;
  const openUnbilled = (): void => {
    setShowUnbilled(true);
    document.getElementById('invoices-unbilled')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  };

  return (
    <PageShell
      title="Invoices"
      maxWidth="bleed"
      testId="invoices-page"
      access={{
        allowed: canView,
        deniedMessage: contactSentence(
          "Invoices aren't part of your access. Ask {contact_admin} if your work needs them.",
          contact,
        ),
      }}
    >
      {/* THE HEADER BAND (LP-34, reweighted in LP-39): the month, the two
          month-end doors (Pre-billing, Payroll hours) as the filled
          buttons, then the month's own act (Compile, and the billing
          file) in outline, and the QuickBooks connection as one quiet
          line. The band's frame
          follows the BODY to the wide width, so band and body share one
          left edge. */}
      <SectionBand
        testId="invoices-band"
        eyebrow="Olive · Billing"
        title="Invoices"
        width="wide"
        headingControls={
          <div data-testid="invoices-month-picker" className="min-w-0 max-w-full [&_label]:sr-only">
            {/* This month picker narrows the list; Compile's "Period start"
                and "Period end" create an invoice. The labels deliberately
                differ so a control that narrows cannot be mistaken for one
                that bills. */}
            <Field label="Invoice month" htmlFor="invoices-month">
              <div className="flex min-w-0 max-w-full flex-wrap items-baseline gap-1">
                <Button
                  variant="ghost-on-dark"
                  size="sm"
                  data-testid="invoices-month-prev"
                  aria-label="Previous month"
                  onClick={() => pickMonth(shiftMonthKey(month, -1))}
                >
                  ‹
                </Button>
                <TextInput
                  id="invoices-month"
                  data-testid="invoices-month"
                  type="month"
                  className={`${INPUT} w-[10rem] min-w-0`}
                  value={month}
                  onChange={(e) => {
                    // Clearing the picker keeps the current month.
                    if (e.target.value !== '') pickMonth(e.target.value);
                  }}
                />
                <Button
                  variant="ghost-on-dark"
                  size="sm"
                  data-testid="invoices-month-next"
                  aria-label="Next month"
                  onClick={() => pickMonth(shiftMonthKey(month, 1))}
                >
                  ›
                </Button>
              </div>
            </Field>
          </div>
        }
        action={
          <div
            data-testid="invoices-band-actions"
            className="flex min-w-0 max-w-full flex-col items-start gap-2 sm:items-end"
          >
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 sm:justify-end">
              {/* Pre-billing and Payroll hours are used more than the
                  billing file (Amanda, LP-39), so they come first and
                  carry the filled on-dark weight. Each keeps its own gate;
                  a seat without them still sees the month's act. */}
              {canSeeMasterPrebilling && (
                <Link
                  data-testid="invoices-prebilling-link"
                  to={`/t/${tenantSlug}/invoices/prebilling`}
                  className={buttonClassName('primary-on-dark', 'sm')}
                >
                  Pre-billing for every client
                </Link>
              )}
              {/* Payroll hours (Amanda, LP-34 round three): the other
                  month-end door; still in the user menu. */}
              {canExportPayrollHours(whoami) && (
                <Link
                  data-testid="invoices-payroll-link"
                  to={`/t/${tenantSlug}/payroll`}
                  className={buttonClassName('primary-on-dark', 'sm')}
                >
                  Payroll hours
                </Link>
              )}
              {/* The month's act, in outline in every month (LP-39): Compile
                  in an invoice month, and the billing file. */}
              {compileInBand && <CompileMonthRunButton state={compileMonth} onDark />}
              {/* Gated inside on view_invoice + view_all_clinical, the
                  population that reads the unbilled report. */}
              <ExportMonthRunButton state={exportMonth} onDark />
            </div>
            <QboConnectionSummary variant="band" />
          </div>
        }
      />
      <div
        data-testid="invoices-body"
        className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} py-6`}
      >
      {/* ONE COLUMN at the wide width: the outcome messages, the month
          line, the filters, then the table, which is the page. Nothing
          sits between the filters and the table but the batch bar.
          min-w-0, so the money table can never push the page wider than
          its frame. */}
      <div data-testid="invoices-main" className="min-w-0 space-y-5">
      {/* The connect-callback result (F11) is an outcome message about
          an action just taken, so it leads the column. */}
      {canView && <QboConnectResultBanner />}
      {/* THE SYNC ERROR (PR 4b), first in the column: every invoice whose
          latest push failed and never landed, from any month and any
          page, read tenant-wide by the server. */}
      {canView && data !== undefined && (
        <PushFailedAlert
          count={data.push_failed_count ?? 0}
          rows={data.push_failed ?? []}
          tenantSlug={tenantSlug}
        />
      )}
      {canView && nativeBillingOff && <NativeBillingOffBanner testId="invoice-native-off-banner" />}
      {/* THE LOUDEST MONEY RISK ON THE PAGE leads the column. A voided
          invoice whose QuickBooks copy is still open can keep charging
          the family. Rides the server's month count under either scope,
          so the live default cannot hide it. */}
      {canView && (
        <VoidCopiesOpenBanner
          testId="invoice-void-copies-banner"
          count={toFixCount}
          lookupCount={data?.copy_lookup_count ?? 0}
          paymentCount={paymentAppliedCount}
          named={namedVoid}
          onShow={showVoidCopies}
        />
      )}

      {rowActionError && (
        <div data-testid="invoice-row-action-error" role="alert" className="text-sm text-pepper">
          {rowActionError}
        </div>
      )}

      {/* COMPILE ONE CLIENT (Amanda, LP-34 round two): a quiet card at the
          top of the body, above the empty state or the table. The
          per-client form serves the case the month's compile cannot, an
          odd period typed by hand: open on a month with no invoice yet,
          folded once the month has invoices to work through. The form's
          values live in the page, so folding it never loses what was
          typed. */}
      {canCompile && !preNativeMonth && (
        <details
          data-testid="invoices-compile-one"
          open={!monthHasInvoices}
          className="rounded-card border border-sage-800/[8%] bg-paper px-4 py-3"
        >
          <summary className="cursor-pointer text-sm font-semibold text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400">
            Compile one client
          </summary>
          <div className="mt-2 space-y-2">
            <p className="text-xs text-ink-soft">
              Pick a client and the dates to bill. The client&rsquo;s Ready notes in those dates become one draft invoice.
            </p>
            <form
              data-testid="invoice-compile-form"
              className="flex flex-wrap items-end gap-3 *:min-w-0"
              onSubmit={(e) => {
                e.preventDefault();
                compileMut.mutate({
                  body: { client_id: clientId.trim(), period_start: periodStart, period_end: periodEnd },
                  payerName: unbilled?.rows.find((r) => r.client_id === clientId.trim())?.bills_with?.display_name ?? null,
                });
              }}
            >
              <Field label="Client" htmlFor="compile-client">
                <SelectField size="field"
                  id="compile-client"
                  data-testid="compile-client"
                  className="w-full"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                >
                  <option value="">Select a client</option>
                  {activeClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.display_name}
                    </option>
                  ))}
                </SelectField>
              </Field>
              <Field label="Period start" htmlFor="compile-period-start">
                <DateInput
                  id="compile-period-start"
                  data-testid="compile-period-start"
                  size="compact" className="max-w-full"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                />
              </Field>
              <Field label="Period end" htmlFor="compile-period-end">
                <DateInput
                  id="compile-period-end"
                  data-testid="compile-period-end"
                  size="compact" className="max-w-full"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                />
              </Field>
              <Button
                type="submit"
                size="sm"
                data-testid="compile-submit"
                loading={compileMut.isPending}
              >
                Compile
              </Button>
            </form>
            {formError && (
              <div data-testid="compile-error" role="alert" className="text-sm text-pepper">
                {formError}
              </div>
            )}
          </div>
        </details>
      )}

      {preNativeMonth && nativeMonth !== null && (
        /* The month's mode, as a tinted line: the tint zone's tokens,
           read as a notice about the month, not a card of work. */
        <p
          data-testid="invoices-pre-native-line"
          className="rounded-card border border-sage-200 bg-sage-50 px-4 py-3 text-sm font-medium text-sage-800"
        >
          {`${monthLabel} is billed through the billing CSV. Invoices in Olive start with ${monthHeadingLabel(nativeMonth)}.`}
        </p>
      )}

      {/* WHAT THE BAND'S ACTS WILL DO AND DID. Before a compile: who is a
          demo client, who gets a second invoice, who is skipped. After:
          one summary line, the names behind "See details". The billing
          file's progress and report sit under it. Absent when there is
          nothing to say. */}
      {hasActivity && (
        <section
          data-testid="invoices-activity"
          aria-label="Compile and billing file"
          className="space-y-3 rounded-card border border-sage-200 bg-sage-50 p-4"
        >
          {showCompileActivity && compileNotes.length > 0 && (
            <div data-testid="compile-month" className="flex flex-col items-start gap-1">
              {compileNotes}
            </div>
          )}
          {showCompileActivity && compileMonth.report !== null && (
            <CompileMonthReportView report={compileMonth.report} collapsible />
          )}
          <ExportMonthProgress state={exportMonth} />
          <ExportMonthReportView state={exportMonth} />
        </section>
      )}

      {/* THE MONTH AT A GLANCE (founder rulings 2026-08-27, 2026-08-28,
          2026-08-31; one line since LP-34). What is LEFT before the month
          can close, split by WHERE the work happens: to send from Olive,
          to fix in QuickBooks, totals Olive couldn't confirm, and the
          clients with notes still to mark Ready.

          THE NUMBERS ARE THE SERVER'S (item 5, F2/F3): month-scoped
          counts off the wire and the month's unbilled report, never a
          tally of the page's rows, never narrowed by the client filter or
          the lifecycle scope. Each count appears only when it is
          non-zero, and the line only when one does: on a clean month the
          absence of the whole line is itself the answer. A pile's button
          also stays while ITS pile is active even if its count has since
          reached zero, so the way out never disappears under the cursor.
          RULING 3 (2026-08-31): an ACTIVE pile keeps its name and count;
          the way out lives on the scope line above the table. */}
      {(pilesShown || notReadyClientCount > 0) && (
        <section
          data-testid="invoices-glance"
          aria-labelledby="invoices-glance-heading"
          className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2"
        >
          <h2 id="invoices-glance-heading" className={GLANCE_TITLE}>
            {month === currentMonth ? 'Before this month closes' : `Before ${monthLabel} closes`}
          </h2>
          {pilesShown && (toSendCount > 0 || monthEndFilter === 'send') && (
            <Button
              variant="secondary"
              size="sm"
              data-testid="invoice-needs-attention-toggle"
              aria-pressed={monthEndFilter === 'send'}
              className={monthEndFilter === 'send' ? 'bg-sage-800/[10%]' : undefined}
              onClick={() => pickPile(monthEndFilter === 'send' ? 'none' : 'send')}
            >
              {`${toSendCount} to send from Olive`}
            </Button>
          )}
          {pilesShown && (toFixCount > 0 || monthEndFilter === 'qbo') && (
            <Button
              variant="secondary"
              size="sm"
              data-testid="invoice-qbo-cleanup-toggle"
              aria-pressed={monthEndFilter === 'qbo'}
              className={monthEndFilter === 'qbo' ? 'bg-sage-800/[10%]' : undefined}
              onClick={() => pickPile(monthEndFilter === 'qbo' ? 'none' : 'qbo')}
            >
              {`${toFixCount} to fix in QuickBooks`}
            </Button>
          )}
          {pilesShown && (unconfirmedCount > 0 || monthEndFilter === 'unconfirmed') && (
            <Button
              variant="secondary"
              size="sm"
              data-testid="invoice-unconfirmed-toggle"
              aria-pressed={monthEndFilter === 'unconfirmed'}
              className={monthEndFilter === 'unconfirmed' ? 'bg-sage-800/[10%]' : undefined}
              onClick={() =>
                pickPile(monthEndFilter === 'unconfirmed' ? 'none' : 'unconfirmed')
              }
            >
              {`${unconfirmedCount} ${
                unconfirmedCount === 1 ? 'total' : 'totals'
              } Olive couldn’t confirm`}
            </Button>
          )}
          {notReadyClientCount > 0 &&
            (canSeeMasterPrebilling ? (
              /* The door to the notes themselves (LP-34 round two): every
                 client's Pre-billing, on this month, filtered to the
                 notes not marked Ready. */
              <Link
                data-testid="invoices-glance-not-ready"
                to={`/t/${tenantSlug}/invoices/prebilling?status=not_ready&month=${month}`}
                className={buttonClassName('secondary', 'sm')}
              >
                {notReadyText}
              </Link>
            ) : (
              /* A seat without that page opens the list below instead. */
              <Button
                variant="secondary"
                size="sm"
                data-testid="invoices-glance-not-ready"
                aria-controls="invoices-unbilled"
                onClick={openUnbilled}
              >
                {notReadyText}
              </Button>
            ))}
        </section>
      )}

      {/* THE TABLE IS THE PAGE: the house card, the one raised surface
          in the body, so it reads above the quiet lines around it. A CSV
          month with nothing in it has no card: its sentence above is the
          whole answer. */}
      {!(preNativeMonth && !showFilters && data !== undefined && data.rows.length === 0) && (
      <section
        aria-label="Invoices"
        data-testid="invoices-table-card"
        className="min-w-0 rounded-card border border-sage-800/[8%] bg-paper p-3 shadow-sh1 sm:p-4"
      >
        {/* THE FILTERS, one row above the table (LP-34): which set the
            table shows, which client, and the status meanings beside
            them. */}
        {showFilters && (
        <div
          data-testid="invoices-filters"
          className="mb-3 flex min-w-0 flex-wrap items-end gap-x-4 gap-y-3 *:min-w-0"
        >
          {/* THE LIFECYCLE SCOPE (D1(b), founder ruling 2026-08-31).
              "Voided" is a place the operator can visit; the default is
              the month's live invoices, and the page ASKS for it
              explicitly rather than riding the server's default. Voided
              rows never stop being work: their count stays in the month
              line above, and the QuickBooks pile surfaces them under
              either scope. */}
          <Field label="Showing" htmlFor="invoices-scope-live">
            <div
              role="group"
              aria-label="Invoice scope"
              className="inline-flex max-w-full overflow-hidden rounded-base border border-sage-300"
            >
              <button
                type="button"
                id="invoices-scope-live"
                data-testid="invoices-scope-live"
                aria-pressed={scope === 'live'}
                onClick={() => pickScope('live')}
                className={`px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sage-400 ${
                  scope === 'live' ? 'bg-sage-800 text-cream' : 'bg-paper text-sage-800 hover:bg-sage-800/[6%]'
                }`}
              >
                This month’s invoices
              </button>
              <button
                type="button"
                data-testid="invoices-scope-void"
                aria-pressed={scope === 'void'}
                onClick={() => pickScope('void')}
                className={`border-l border-sage-300 px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sage-400 ${
                  scope === 'void' ? 'bg-sage-800 text-cream' : 'bg-paper text-sage-800 hover:bg-sage-800/[6%]'
                }`}
              >
                Voided
              </button>
            </div>
          </Field>
          {/* A filter needs two real choices to move between; with one
              active client it is furniture, and its options would shadow
              the compile picker's. Rides the same roster gate as the
              compile picker, which is the only feed it has. Labelled
              "Only this client" so it can never be read as the compile
              form's "Client", which bills. */}
          {canCompile && canViewAllClinical(whoami) && activeClients.length > 1 && (
            <Field label="Only this client" htmlFor="invoices-client">
              <SelectField size="compact"
                id="invoices-client"
                data-testid="invoices-client"
                className="w-full"
                value={clientFilter}
                onChange={(e) => pickClient(e.target.value)}
              >
                <option value="">Every client</option>
                {activeClients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </SelectField>
            </Field>
          )}
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 pb-1 sm:ml-auto">
            <StatusLegend open={showLegend} onToggle={() => setShowLegend((v) => !v)} onClose={() => setShowLegend(false)} />
          </div>
        </div>
        )}
        {/* The ruled batch action. It is a loop over the same per-invoice
            push, so a batch of twenty with three problems is seventeen
            pushed and three named failures.

            Mounted UNCONDITIONALLY for a pusher (contract item 1, F2):
            the payload gate empties the selection on the refetch after a
            send, and for a one-invoice batch that would have unmounted
            the bar and taken the report with it, which is verbatim the
            founder regression of 2026-08-26. The bar renders nothing
            itself until it has a selection or a report to show. */}
        {canBatchSelect && (
          <div className="mb-3">
            <BatchPushBar
              selected={selectedTargets}
              drafts={selectedDraftTargets}
              approved={selectedApprovedTargets}
              onDone={() => {
                // Do NOT clear the selection here. The refetch this
                // fires returns each acted-on invoice under its new
                // status (or holding its QuickBooks id), and the payload
                // gates above drop it from its act's subset on their
                // own; clearing the whole selection would also throw
                // away the rows the act did NOT reach, which the
                // operator still means to move.
                invalidateList();
              }}
            />
          </div>
        )}
        {/* THE SCOPE LINE (ruling 3, 2026-08-31). While a pile narrows the
            table, a line directly above it names the pile and carries the
            way back as its OWN affordance; the pile's label stays put in
            the month line. The Voided scope needs no line (LP-34): the
            Showing control beside it says which set is shown and is the
            way back. */}
        {monthEndFilter !== 'none' && (
          <div
            data-testid="invoices-scope-line"
            className="mb-2 flex flex-wrap items-center gap-2 text-sm text-ink-soft"
          >
            <span>
              {monthEndFilter === 'send'
                ? 'Left to send.'
                : monthEndFilter === 'qbo'
                  ? 'Showing what is left to fix in QuickBooks.'
                  : 'Totals still to check against QuickBooks.'}
            </span>
            <TextAction data-testid="invoices-show-all" onClick={() => pickPile('none')}>
              Show every invoice
            </TextAction>
          </div>
        )}
        <AsyncBoundary
          isLoading={isPending}
          isError={isError}
          error={error}
          onRetry={() => void refetch()}
          isEmpty={data !== undefined && data.rows.length === 0 && !monthEmpty && !(preNativeMonth && !filtersOn)}
          emptyHeadline={
            /* An emptied pile or scope is not the empty-month line: that
               sentence is a claim about the month, and the month may
               have plenty. The area names the slice and the month
               instead, so the empty state can never read as a clean
               month by accident. */
            monthEndFilter !== 'none'
              ? `Nothing left in this pile for ${monthLabel}.`
              : scope === 'void'
                ? `No voided invoices for ${monthLabel}.`
                : clientFilter !== ''
                  ? `No invoices for this client in ${monthLabel}.`
                  : /* the month's invoices are all voided (an empty month
                       with nothing narrowed takes the empty state below) */
                    `Only voided invoices for ${monthLabel}. Choose Voided to see them.`
          }
        >
      {monthEmpty && (
        /* THE EMPTY MONTH CARRIES THE ACT (LP-34 round two). Notes ready:
           Compile, here. None ready: where they get ready. A seat that
           cannot compile is told only that there is nothing yet. */
        <div data-testid="invoices-empty" className="flex flex-col items-center gap-3 py-8 text-center">
          {compileInEmpty ? (
            <>
              <p className="text-sm font-medium text-ink">No invoices for this month yet.</p>
              <CompileMonthRunButton state={compileMonth} />
            </>
          ) : canCompile && (!canClinical || unbilled !== undefined) ? (
            <>
              <p className="max-w-prose text-sm font-medium text-ink">
                No notes are ready to bill for this month yet. Once notes are marked Ready on Pre-billing, compile them here.
              </p>
              {canSeeMasterPrebilling && (
                <Link
                  data-testid="invoices-empty-prebilling-link"
                  to={`/t/${tenantSlug}/invoices/prebilling`}
                  className={buttonClassName('secondary', 'sm')}
                >
                  Pre-billing for every client
                </Link>
              )}
            </>
          ) : (
            <p className="text-sm font-medium text-ink">No invoices for this month yet.</p>
          )}
        </div>
      )}
      {data && data.rows.length > 0 && (
        /* THE SAFETY VALVE (cockpit train). overflow-x-auto is the house
           answer, used by ~20 surfaces (PreBilling's is the nearest
           neighbour): on a phone the table scrolls inside this wrapper
           and the page never scrolls sideways. The row menu draws in the
           overlay layer, so this wrapper never clips it. */
        /* relative: the header's sr-only label is absolutely placed, and
           an absolute box whose containing block lies outside this
           wrapper escapes its clip and widens the page on a phone. */
        <div data-testid="invoices-table-scroll" className="relative overflow-x-auto">
        <table data-testid="invoices-table" className="w-full text-sm">
          <thead>
            <tr className={`${TABLE_HEAD_ROW} border-b border-sage-800/[8%]`}>
              {canBatchSelect && (
                <th className="py-2 pr-3 w-6">
                  {/* The testid keeps its historical name; the control
                      now selects every row with a pending step the seat
                      can perform, over the SAME rows the table shows. */}
                  <input
                    type="checkbox"
                    data-testid="invoice-select-all-issued"
                    aria-label="Select every invoice with a pending step"
                    checked={allSelectableSelected}
                    disabled={selectableRows.length === 0}
                    onChange={toggleAllSelectable}
                  />
                </th>
              )}
              {/* The client leads the row (founder feedback 2026-07-10);
                  the doc # stays on the row, under the name (LP-34). */}
              <th className="py-2 pr-4 font-semibold">Client</th>
              <th className="py-2 pr-4 font-semibold">Status</th>
              {/* Notes in the invoice's period not marked Ready yet, a
                  quiet count in its own column (LP-34). */}
              <th className="py-2 pr-4 font-semibold text-right">Not ready</th>
              <th className="py-2 pr-4 font-semibold">Period</th>
              <th className="py-2 pr-4 font-semibold text-right">Total</th>
              {/* THE STAGE-7 COLUMN (founder ruling 2026-08-27). Rides
                  view_invoice, not push_invoice: seeing that a month is
                  unsent is not the same authority as sending it. */}
              {canView && <th className="py-2 pr-4 font-semibold">QuickBooks</th>}
              {/* ONE labelled action column (cockpit train): a header a
                  screen reader announces as nothing is a column an
                  operator cannot navigate to. Its cells hold the row's
                  one primary (Preview) and its menu. */}
              <th className="py-2 pl-3 font-semibold text-right">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sage-800/[6%]">
            {visibleRows.map((inv) => {
              const name = inv.client_display_name;
              const notReady = inv.status !== 'void' && inv.not_ready_count !== null && inv.not_ready_count > 0
                ? inv.not_ready_count
                : null;
              return (
              <tr key={inv.id} className="align-top">
                {canBatchSelect && (
                  <td className="py-2.5 pr-3">
                    {selectableIds.has(inv.id) && (
                      <input
                        type="checkbox"
                        data-testid={`invoice-select-${inv.id}`}
                        aria-label={`Select ${name || 'this invoice'}`}
                        checked={selectedIds.has(inv.id)}
                        onChange={() => toggleOne(inv.id)}
                      />
                    )}
                  </td>
                )}
                <td
                  className="py-2.5 pr-4 font-medium text-ink"
                  data-testid={`invoice-client-${inv.id}`}
                >
                  {/* The name opens the client's record, for a seat that
                      reads every client; the demo chip stays outside it. */}
                  {canOpenEveryClientRecord(whoami) && name !== '' ? (
                    <Link
                      data-testid={`invoice-client-link-${inv.id}`}
                      className="text-sage-800 underline underline-offset-2"
                      to={`/t/${tenantSlug}/clients/${inv.client_id}`}
                    >
                      {name}
                    </Link>
                  ) : name !== '' ? (
                    name
                  ) : (
                    '-'
                  )}
                  {inv.client_is_demo && (
                    <>
                      {' '}
                      <Chip variant="neutral" data-testid={`invoice-row-demo-${inv.id}`}>
                        Demo client
                      </Chip>
                    </>
                  )}
                  {inv.doc_number !== null && (
                    <span
                      data-testid={`invoice-doc-${inv.id}`}
                      className="block text-xs font-normal text-ink-soft tabular-nums"
                    >
                      {`#${inv.doc_number}`}
                    </span>
                  )}
                </td>
                <td className="py-2.5 pr-4">
                  <Chip
                    variant={invoiceStatusVariant(inv.status)}
                    data-testid={`invoice-status-${inv.id}`}
                  >
                    {invoiceStatusLabel(inv.status)}
                  </Chip>
                </td>
                <td className="py-2.5 pr-4 text-right tabular-nums">
                  {/* The server's count, null where it is not served;
                      nothing at 0 and nothing on a void row. */}
                  {notReady !== null && (
                    <span
                      data-testid={`invoice-row-not-ready-${inv.id}`}
                      title={notReady === 1 ? '1 note not marked Ready' : `${notReady} notes not marked Ready`}
                      className="text-ink"
                    >
                      {notReady}
                    </span>
                  )}
                </td>
                <td className="py-2.5 pr-4 whitespace-nowrap text-ink-soft">
                  {periodLabel(inv.period_start, inv.period_end, month)}
                </td>
                <td className="py-2.5 pr-4 text-right tabular-nums">${inv.total}</td>
                {canView && (
                  <td className="py-2.5 pr-4">
                    <PushStatusCell row={inv} now={now} contact={contact} />
                  </td>
                )}
                <td className="py-1.5 pl-3">
                  <div className="flex items-center justify-end gap-1">
                    {/* The row's one primary: the invoice itself, where
                        it is reviewed, approved and issued. NAVIGATION,
                        so it keeps its underline. */}
                    <Link
                      data-testid={`invoice-link-${inv.id}`}
                      className="px-1 text-sage-800 underline underline-offset-2 whitespace-nowrap"
                      to={`/t/${tenantSlug}/invoices/${inv.id}`}
                    >
                      Preview
                    </Link>
                    {/* THE ROW MENU (LP-34): the row's other actions,
                        each with the gate, handler and test id it had
                        inline. The destructive openers keep the
                        sanctioned pepper-deep register (cockpit train);
                        danger itself stays each confirm dialog's own
                        submit. */}
                    <RowMenu label={`More for ${name || 'this invoice'}`} testId={`invoice-row-menu-${inv.id}`}>
                      {(close) => (
                        <>
                          <Link
                            role="menuitem"
                            data-testid={`invoice-billing-details-${inv.id}`}
                            className={textActionClass}
                            to={`/t/${tenantSlug}/invoices/clients/${inv.client_id}`}
                            onClick={close}
                          >
                            Billing details
                          </Link>
                          {inv.status === 'draft' && canCompile && (
                            <DestructiveTextAction
                              role="menuitem"
                              data-testid={`invoice-discard-${inv.id}`}
                              onClick={() => {
                                close();
                                setDiscardTarget(inv);
                              }}
                            >
                              Discard
                            </DestructiveTextAction>
                          )}
                          {inv.status === 'approved' && canApprove && (
                            <TextAction
                              role="menuitem"
                              data-testid={`invoice-reopen-${inv.id}`}
                              onClick={() => {
                                close();
                                reopenMut.mutate(inv.id);
                              }}
                            >
                              Reopen
                            </TextAction>
                          )}
                          {inv.status === 'issued' && canVoid && (
                            <DestructiveTextAction
                              role="menuitem"
                              data-testid={`invoice-void-open-${inv.id}`}
                              onClick={() => {
                                close();
                                setVoidTarget(inv);
                              }}
                            >
                              Void
                            </DestructiveTextAction>
                          )}
                        </>
                      )}
                    </RowMenu>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
        </AsyncBoundary>

        {/* Prev/Next off the server's honest total (item 5): the shared
            footer extracted from CompletedView, on the same ruled page
            size. `total` is the filtered set's size, so paging inside a
            pile walks every row the count counted. */}
        {data && data.total > 0 && (
          <PaginationFooter
            offset={offset}
            pageSize={PAGE_SIZE}
            total={data.total}
            onOffsetChange={setOffset}
            testIdPrefix="invoices-page-"
          />
        )}
      </section>
      )}

        {/* THE UNBILLED-WORK SECTION, under the invoice table: the money
            that is NOT on the list above, which only makes sense read
            after it. Collapsed to start, with its count.

            NOT a pile filter: the table above is FROM invoices, and a
            client with no invoice has no row to filter to, so the line
            expands in place to its own table. Absence is the zero state,
            with ONE exception: zero rows with Ready notes from before
            native billing still renders the line, worded about the notes,
            because those notes bill only through the CSV and a silent
            month would read as clean. */}
        <section id="invoices-unbilled" aria-label="Unbilled work" className="min-w-0 scroll-mt-24">
          {canView && !canClinical && (
            /* The refusal is VISIBLE and driven by the seat, never by a
               query error: rendered from whoami before any request, in
               place of the line, so a refused seat can never read as a
               clean month. */
            <p data-testid="invoice-unbilled-refused" className="text-xs text-ink-soft">
              {contactSentence(
                "This report reads every client's notes, which isn't part of your access. Ask {contact_admin} if your work needs it.",
                contact,
              )}
            </p>
          )}
          {canClinical &&
            unbilled !== undefined &&
            (unbilled.rows.length > 0 || unbilled.pre_native_ready_count > 0) && (
            <div className="border-t border-sage-800/[8%] pt-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {unbilled.rows.length > 0 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="invoice-unbilled-toggle"
                    aria-expanded={showUnbilled}
                    aria-controls="invoice-unbilled-table"
                    className="-ml-3.5"
                    icon={
                      <ChevronRight
                        size={14}
                        aria-hidden="true"
                        className={`transition-transform ${showUnbilled ? 'rotate-90' : ''}`}
                      />
                    }
                    onClick={() => setShowUnbilled((v) => !v)}
                  >
                    {`${unbilled.rows.length} ${
                      unbilled.rows.length === 1 ? 'client' : 'clients'
                    } with unbilled work`}
                  </Button>
                ) : null}
                {/* The notes from before native billing: one sentence,
                    the whole line when no client has other unbilled work
                    (LP-34 round two). */}
                {unbilled.pre_native_ready_count > 0 && (
                  <span
                    data-testid="invoice-unbilled-period-note"
                    className={unbilled.rows.length > 0 ? 'text-xs text-ink-soft' : 'py-1 text-sm text-ink-soft'}
                  >
                    {preNativeNote(unbilled.pre_native_ready_count, unbilled.native_from)}
                  </span>
                )}
              </div>
              {showUnbilled && unbilled.rows.length > 0 && (
                <div className="relative mt-2 overflow-x-auto">
                <table id="invoice-unbilled-table" data-testid="invoice-unbilled-table" className="w-full text-sm">
                  <thead>
                    <tr className={`${TABLE_HEAD_ROW} border-b border-sage-800/[8%]`}>
                      <th className="py-2 pr-4 font-semibold">Client</th>
                      <th className="py-2 pr-4 font-semibold text-right">Amount</th>
                      <th className="py-2 pr-4 font-semibold text-right">Hours</th>
                      <th className="py-2 font-semibold">Reasons</th>
                    </tr>
                  </thead>
                  {/* Rows render in the order the server sent them:
                      amount DESC then name is the server's triage order,
                      and re-sorting here would let two loads disagree. */}
                  <tbody className="divide-y divide-sage-800/[6%]">
                    {unbilled.rows.map((row) => (
                      <tr
                        key={row.client_id}
                        data-testid={`invoice-unbilled-row-${row.client_id}`}
                        className="align-top"
                      >
                        <td className="py-2 pr-4 text-ink">
                          <span className="font-medium">{row.client_display_name}</span>
                          {' '}
                          <Link
                            data-testid={`invoice-unbilled-billing-details-${row.client_id}`}
                            className="text-xs text-sage-700 underline whitespace-nowrap"
                            to={`/t/${tenantSlug}/invoices/clients/${row.client_id}`}
                          >
                            Billing details
                          </Link>
                          {row.is_demo && (
                            <>
                              {' '}
                              <Chip variant="neutral" data-testid={`invoice-unbilled-demo-${row.client_id}`}>
                                Demo client
                              </Chip>
                            </>
                          )}
                          {row.is_archived && (
                            /* The compile picker lists active clients
                               only, so naming an archived client without
                               saying this sends the operator to a
                               dropdown the client is not in. */
                            <span className="block text-xs text-ink-soft">
                              Archived. Un-archive the client before this work can be compiled.
                            </span>
                          )}
                          {/* A couple on one invoice (0215): this client's
                              work goes on its payer's invoice, from the
                              row's own bills_with. */}
                          {row.bills_with != null && (
                            <span
                              className="block text-xs text-ink-soft"
                              data-testid={`invoice-unbilled-bills-with-${row.client_id}`}
                            >
                              {`Combined with ${row.bills_with.display_name}: this work goes on ${row.bills_with.display_name}'s invoice.`}
                            </span>
                          )}
                        </td>
                        {/* Money renders from the WIRE TEXT, never back
                            through a float. Blank and $0.00 are different
                            answers: blank means no event to price, $0.00
                            is a priced zero. */}
                        <td className="py-2 pr-4 text-right tabular-nums text-ink">
                          {row.amount === null ? '' : `$${row.amount}`}
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums text-ink">
                          {row.hours === null ? '' : row.hours}
                        </td>
                        <td className="py-2 text-xs text-ink-soft">
                          {row.reasons.map((r) => unbilledReasonLine(r, row, monthLabel, contact)).join(' ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* The foot is the SERVER's total over the whole month,
                      never the page's rows re-added. */}
                  <tfoot>
                    <tr
                      data-testid="invoice-unbilled-total"
                      className="border-t border-sage-200 font-semibold text-ink"
                    >
                      <td className="py-2 pr-4">Total</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{`$${unbilled.total_amount}`}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{unbilled.total_hours}</td>
                      <td className="py-2" />
                    </tr>
                  </tfoot>
                </table>
                </div>
              )}
            </div>
          )}
        </section>

      </div>
      </div>

      {discardTarget && (
        <ConfirmDialog
          title="Discard this draft?"
          body={`This deletes the draft. Its charges return to ${discardTarget.client_display_name || 'the client'}'s Pre-billing tab as Ready lines.`}
          confirmLabel="Discard draft"
          destructive
          onConfirm={() => {
            const id = discardTarget.id;
            setDiscardTarget(null);
            discardMut.mutate(id);
          }}
          onCancel={() => setDiscardTarget(null)}
        />
      )}

      {voidTarget && (
        <VoidInvoiceDialog
          tenantSlug={tenantSlug}
          invoiceId={voidTarget.id}
          docNumber={voidTarget.doc_number}
          clientName={voidTarget.client_display_name || 'the client'}
          qboInvoiceId={voidTarget.qbo_invoice_id}
          onVoided={(invoice) => {
            setVoidTarget(null);
            setRowActionError(null);
            setJustVoided({ id: invoice.id, docNumber: invoice.doc_number });
            invalidateList();
          }}
          onClose={() => setVoidTarget(null)}
        />
      )}
    </PageShell>
  );
}

/**
 * "What do the statuses mean?", a small link by the filters that opens
 * the status and QuickBooks-column meanings in a popover (LP-34; it used
 * to be a panel of its own). One toggle governs both lists.
 */
function StatusLegend({
  open,
  onToggle,
  onClose,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}): ReactNode {
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  return (
    <>
      <Button
        ref={toggleRef}
        variant="ghost"
        size="sm"
        data-testid="invoice-status-legend-toggle"
        className="-ml-3.5 sm:ml-0"
        aria-expanded={open}
        aria-controls={open ? 'invoice-status-legend-popover' : undefined}
        onClick={onToggle}
      >
        {open ? 'Hide status meanings' : 'What do the statuses mean?'}
      </Button>
      {open && (
        <AnchoredLayer
          anchorRef={toggleRef}
          onClose={onClose}
          id="invoice-status-legend-popover"
          testId="invoice-status-legend-popover"
          role="region"
          ariaLabel="What the statuses mean"
          className="w-[36rem] rounded-card border border-sage-800/[8%] bg-paper p-4 shadow-sh3 focus:outline-none"
          focusOnOpen
        >
          <dl data-testid="invoice-status-legend" className="space-y-1 text-xs text-ink-soft">
            {INVOICE_STATUS_LEGEND.map(({ status, meaning }) => (
              <div key={status} className="flex gap-2">
                <dt className="min-w-[4.5rem] font-semibold text-sage-800">
                  {invoiceStatusLabel(status)}
                </dt>
                <dd>{meaning}</dd>
              </div>
            ))}
          </dl>
          {/* The QuickBooks column earns its own block (TRUTH-FIX
              train): the page grew a whole column the legend never
              mentioned. */}
          <p className="mt-3 text-xs font-semibold text-sage-800">
            What the QuickBooks column means
          </p>
          <dl data-testid="invoice-qbo-legend" className="mt-1 space-y-1 text-xs text-ink-soft">
            {QBO_COLUMN_LEGEND.map(({ label, meaning }) => (
              <div key={label} className="flex gap-2">
                <dt className="min-w-[9.5rem] font-semibold text-sage-800">{label}</dt>
                <dd>{meaning}</dd>
              </div>
            ))}
          </dl>
        </AnchoredLayer>
      )}
    </>
  );
}
